import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { PAGE_URLS } from '@/lib/constants'
import { content } from '@/lib/content'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const navLinks = [
  { name: 'Home', url: PAGE_URLS.HOME },
  {
    name: 'Events',
    sublinks: [
      { name: 'Weekly Events', url: PAGE_URLS.ACTIVITIES },
    ],
  },
  { name: 'Services', url: PAGE_URLS.SERVICES },
  {
    name: 'Facilities',
    sublinks: [{ name: 'Available Facilities', url: PAGE_URLS.FACILITIES }],
  },
  { name: 'Gallery', url: PAGE_URLS.GALLERY },
  { name: 'Contact', url: PAGE_URLS.CONTACT },
  {
    name: 'Menus',
    sublinks: [
      { name: 'Fish Fry Menu', url: PAGE_URLS.FISHFRY_MENU, external: true },
      { name: 'Fish Fry Lunch Menu', url: PAGE_URLS.FISHFRY_LUNCH_MENU, external: true },
      { name: 'Lent Fish Fry Menu', url: PAGE_URLS.LENT_FISHFRY_MENU, external: true },
      { name: 'Weekly Menu', url: PAGE_URLS.WEEKLY_MENU, external: true },
    ],
  },
]

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-lg'
          : 'bg-transparent'
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-bold text-primary transition-colors hover:text-primary/80"
          >
            {content.navbar.logo}
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList>
              {navLinks.map((link) => (
                <NavigationMenuItem key={link.name}>
                  {link.sublinks ? (
                    <>
                      <NavigationMenuTrigger
                        className="font-medium text-foreground bg-transparent hover:bg-secondary hover:text-foreground data-[state=open]:bg-secondary"
                      >
                        {link.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[200px] gap-1 p-2 bg-card border border-border">
                          {link.sublinks.map((sublink) => (
                            <li key={sublink.name}>
                              {sublink.external ? (
                                <a
                                  href={sublink.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block select-none rounded-md p-3 text-sm font-medium leading-none text-foreground no-underline outline-none transition-colors hover:bg-secondary hover:text-foreground focus:bg-secondary"
                                >
                                  {sublink.name}
                                </a>
                              ) : (
                                <Link
                                  to={sublink.url}
                                  className="block select-none rounded-md p-3 text-sm font-medium leading-none text-foreground no-underline outline-none transition-colors hover:bg-secondary hover:text-foreground focus:bg-secondary"
                                >
                                  {sublink.name}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <NavigationMenuLink asChild>
                      <Link
                        to={link.url}
                        className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary hover:text-foreground focus:bg-secondary focus:outline-none"
                      >
                        {link.name}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Mobile Menu */}
          <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground hover:bg-secondary cursor-pointer"
              >
                <Menu className="h-6 w-6" />
                <span className="sr-only">{content.navbar.mobileMenuTrigger}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-card border-border">
              <SheetHeader>
                <SheetTitle className="text-primary">{content.navbar.mobileMenuTitle}</SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col space-y-3">
                {navLinks.map((link) => (
                  <div key={link.name}>
                    {link.sublinks ? (
                      <Collapsible>
                        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary cursor-pointer">
                          {link.name}
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2 pl-4">
                          {link.sublinks.map((sublink) => (
                            sublink.external ? (
                              <a
                                key={sublink.name}
                                href={sublink.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                                onClick={() => setIsMobileOpen(false)}
                              >
                                {sublink.name}
                              </a>
                            ) : (
                              <Link
                                key={sublink.name}
                                to={sublink.url}
                                className="block rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                                onClick={() => setIsMobileOpen(false)}
                              >
                                {sublink.name}
                              </Link>
                            )
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <Link
                        to={link.url}
                        className="block rounded-md px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary"
                        onClick={() => setIsMobileOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  )
}
